<template>
  <div class="row">
    <div class="col-lg-12">
      <AppTable
        ref="table"
        hoverable
        table-classes="table-sm-responsive"
        :loading="showTableLoading"
        :table-data="autoTrackRules"
      >
        <template #empty>
          <div
            id="no-data"
            class="text-center"
          >
            <img
              :src="imageNoDataPlaceholder"
              width="250"
            />
            <template v-if="activeTabSelected">
              <h4 class="mt-0">No auto start/stop policies</h4>
              <p class="text-lighter mb-30 text-13">
                Set up rules for your team to start and stop their desktop timer automatically
              </p>
            </template>
            <template v-else>
              <p class="mb-30 text-14">No archived auto start/stop policies</p>
            </template>
          </div>
        </template>
        <AppTableColumn
          v-slot="{ row }"
          header="Policy"
          name="rule"
        >
          <span>
            {{ row.name }}
            <div
              v-if="row.auto_add_new_users"
              class="text-lighter small"
            >
              (Automatic added members)
            </div>
          </span>
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Members"
          name="members"
        >
          <template v-if="activeTabSelected">
            <a
              v-tooltip="true"
              class="d-flex-inline align-items-center hover-link a-tooltip members-link"
              title="Edit members"
              data-original-title="Edit members"
              @click="$emit('edit-rule', row.id)"
            >
              {{ row.member_ids?.length }}
              <i class="hi hi-edit-xs mx-5" />
            </a>
          </template>
          <template v-else>
            {{ row.member_ids?.length }}
          </template>
          <span
            v-if="row.pending_members_count > 0"
            v-tooltip="true"
            class="text-lighter ml-5 members-pending"
            data-testid="members-pending"
            title="Members are being added in the background"
          >
            +{{ row.pending_members_count }} adding
          </span>
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Type"
          name="type"
        >
          {{ activatedByText(row.activated_by) }}
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Device"
          name="device"
        >
          <span>
            {{ appTypeDetails(row.app_type).text }}
            <div class="text-lighter small">
              {{ appTypeDetails(row.app_type).description }}
            </div>
          </span>
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header=""
        >
          <AppDropdown
            class="pull-right table-actions-dropdown"
            direction="right"
            label="Actions"
            track
            track-scope="Auto track rules table"
            size="small"
          >
            <AppDropdownItem
              v-if="activeTabSelected"
              track-as="Edit auto-track rule"
              @click="$emit('edit-rule', row.id)"
            >
              Edit
            </AppDropdownItem>
            <AppDropdownItem
              :track-as="`${toggleRuleStatusText} auto-track rule`"
              @click="openWarningDialog(row)"
            >
              <div class="d-flex flex-column">
                {{ toggleRuleStatusText }}
              </div>
            </AppDropdownItem>
          </AppDropdown>
        </AppTableColumn>
      </AppTable>
    </div>
    <AutoTrackRuleWarningDialog
      :auto-track-rule="selectedRule"
      :show="showWarningDialog"
      @close="closeWarningDialog"
      @status-change-accepted="changeStatus"
    />
  </div>
</template>

<script>
import { IMAGE_EMPTY_PLAYLIST } from '~/rails_assets';
import AutoTrackRuleWarningDialog from './AutoTrackRuleWarningDialog.vue';

export default {
  name: 'AutoTrackRulesTable',
  components: {
    AutoTrackRuleWarningDialog,
  },
  props: {
    autoTrackRules: {
      type: Array,
      required: true,
    },
    showTableLoading: {
      type: Boolean,
      required: true,
    },
    status: {
      type: String,
      required: true,
    },
    autoTrackOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['edit-rule', 'rule-updated'],
  data() {
    return {
      imageNoDataPlaceholder: IMAGE_EMPTY_PLAYLIST,
      selectedRule: {},
      showWarningDialog: false,
    };
  },
  computed: {
    toggleRuleStatusText() {
      return this.status === 'active' ? 'Archive' : 'Restore';
    },
    activeTabSelected() {
      return this.status === 'active';
    },
  },
  methods: {
    activatedByText(activatedBy) {
      const option = this.autoTrackOptions.find((option) => option.id === activatedBy);

      return option ? option.text : '-';
    },
    appTypeDetails(appType) {
      const devices = {
        standard: {
          text: 'Personal computer',
          description: '(Desktop app)',
        },
        corporate: {
          text: 'Company computer',
          description: '(Silent app)',
        },
      };

      return devices[appType];
    },
    openWarningDialog(autoTrackRule) {
      this.selectedRule = autoTrackRule;
      this.showWarningDialog = true;
    },
    changeStatus() {
      this.$emit('rule-updated', this.selectedRule.id);
      this.closeWarningDialog();
    },
    closeWarningDialog() {
      this.selectedRule = {};
      this.showWarningDialog = false;
    },
  },
};
</script>
